import React, { useState, useEffect } from "react";
import axios from "axios";
import TeacherCard from "./components/TeacherCard";
import { Search } from "lucide-react";
import Lottie from "lottie-react";
import Loading from "../../assets/Loading.json";
import { host } from "../../api/ApiCalls";

const Teachers = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [minRating, setMinRating] = useState(0);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(9);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await axios.get(`${host}/api/teachers`);
        const sortedTeachers = response.data.sort((a, b) => {
          // Step 1: Compare ratings
          if (b.rating !== a.rating) {
            return b.rating - a.rating;
          }

          // Step 2: Compare offeredCourses length (handle missing arrays)
          const aCourses = a.offeredCourses?.length || 0;
          const bCourses = b.offeredCourses?.length || 0;

          return bCourses - aCourses;
        });

        setTeachers(sortedTeachers);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch teachers");
        setLoading(false);
      }
    };

    fetchTeachers();
  }, []);

  const filteredTeachers = teachers.filter((teacher) => {
    const matchesSearch = teacher.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesRating = teacher.rating >= minRating;
    return matchesSearch && matchesRating;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-gray-800">
          Find Your Perfect Teacher
        </h1>
        <p className="text-gray-500 mt-2">
          Browse expert teachers rated by students
        </p>
      </div>

      <div className="flex flex-col md:flex-row justify-between gap-4 mb-10">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-5 w-5" />
          <input
            type="text"
            placeholder="Search teachers by name..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor="rating"
            className="text-gray-700 font-medium whitespace-nowrap"
          >
            Minimum Rating:
          </label>
          <select
            id="rating"
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={minRating}
            onChange={(e) => setMinRating(Number(e.target.value))}
          >
            {[0, 1, 2, 3, 4, 5].map((r) => (
              <option key={r} value={r}>
                {r}+
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center text-gray-800">
          <div className="w-80">
            <Lottie animationData={Loading} loop={true} />
          </div>
        </div>
      ) : error ? (
        <p className="text-red-500 text-center">{error}</p>
      ) : filteredTeachers.length === 0 ? (
        <p className="text-gray-500 text-center">
          No teachers match your criteria.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTeachers.slice(0, visibleCount).map((teacher) => (
            <TeacherCard key={teacher._id} teacher={teacher} />
          ))}
        </div>
      )}
      {visibleCount < filteredTeachers.length && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setVisibleCount((prev) => prev + 6)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-full"
          >
            Show More
          </button>
        </div>
      )}
    </div>
  );
};

export default Teachers;
